import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './A_Notification.module.css';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import cn from 'classnames';

export interface A_NotificationProps {
  type: 'success' | 'error';
  message: string;
  duration?: number;
  onClose?: () => void;
  isVisible?: boolean;
}

export default function A_Notification({
  type,
  message,
  duration = 3000,
  onClose,
  isVisible = true,
}: A_NotificationProps) {
  const [isShowing, setIsShowing] = useState(isVisible);
  const [isHiding, setIsHiding] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const onCloseRef = useRef(onClose);
  const prevVisibleRef = useRef(false);
  const isShowingRef = useRef(isVisible);
  const mountedRef = useRef(true);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Не очищаем таймеры здесь: в Strict Mode React вызывает cleanup при
      // «двойном монтировании», и таймер успевает сброситься до срабатывания.
      // При реальном unmount таймер сработает позже — не вызовем setState
      // благодаря проверке mountedRef в колбэке.
    };
  }, []);

  useEffect(() => {
    const wasVisible = prevVisibleRef.current;
    prevVisibleRef.current = isVisible;
    const currentIsShowing = isShowingRef.current;

    if (isVisible && !wasVisible) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setIsShowing(true);
      isShowingRef.current = true;
      setIsHiding(false);

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (!mountedRef.current) return;
        setIsHiding(true);
        hideTimerRef.current = setTimeout(() => {
          hideTimerRef.current = null;
          if (!mountedRef.current) return;
          setIsShowing(false);
          isShowingRef.current = false;
          onCloseRef.current?.();
        }, 300);
      }, duration);
    } else if (!isVisible && wasVisible) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (currentIsShowing) {
        setIsHiding(true);
        hideTimerRef.current = setTimeout(() => {
          hideTimerRef.current = null;
          setIsShowing(false);
          isShowingRef.current = false;
          onCloseRef.current?.();
        }, 300);
      }
    }

    return () => {
      if (isVisible) return;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [isVisible, duration]);

  if (!isShowing) {
    return null;
  }

  const Icon =
    type === 'success'
      ? Q_Icon.Q_IndicatorSuccessIcon
      : Q_Icon.Q_IndicatorErrorIcon;

  return createPortal(
    <div
      className={cn(styles.wrapper, styles[type], isHiding && styles.hiding)}
    >
      <div className={styles.icon_wrapper}>
        <Icon width={24} height={24} fill='none' />
      </div>
      <span className={cn(styles.message, 'text-p-2')}>{message}</span>
    </div>,
    document.body
  );
}
